<template>
  <div ref="scroll">
    <slot name="topLoader" />
    <div ref="top" />
    <slot />
    <div ref="bottom" data-test="bottom-loader" />
    <slot name="bottomLoader" />
  </div>
</template>

<script lang="ts">
import type { PropOptions } from 'vue';
import Vue from 'vue';
import { isElementInViewport } from './utils';

interface IData {
  threshold: number;
  containerElement: HTMLElement | null;
  containerElementStrict: HTMLElement | Document | null;
}

const MOBILE_THRESHOLD = 1000;
const DESKTOP_THRESHOLD = 1500;

export default Vue.extend({
  name: 'BravadoInfiniteScrollBase',
  props: {
    loading: {
      type: Boolean,
      required: true,
      default: false,
    } as PropOptions<boolean>,
    /** Must be a valid query selector.
     * @example .element__scroll | #scrollElement */
    container: {
      type: String,
      default: '',
    } as PropOptions<string>,
    scrollHandler: {
      type: Function,
      default: () => null,
    },
    containerThreshold: {
      type: Number,
      default: 0,
    } as PropOptions<number>,
    skipHorizontalCheck: {
      type: Boolean,
      default: false,
    } as PropOptions<boolean>,
  },
  data(): IData {
    return {
      threshold: this.$device.isMobile ? MOBILE_THRESHOLD : DESKTOP_THRESHOLD,
      containerElement: null,
      containerElementStrict: null,
    };
  },
  mounted() {
    this.containerElementStrict = this.getContainerElementStrict();
    if (this.containerElementStrict) {
      this.containerElementStrict.addEventListener('scroll', this.handleScroll, { passive: true });
    }

    this.containerElement = this.getContainerElement();
    this.$emit('on-container-element-ready', this.containerElement);
  },
  beforeDestroy() {
    if (this.containerElementStrict) {
      this.containerElementStrict.removeEventListener('scroll', this.handleScroll);
    }
  },
  methods: {
    handleScroll() {
      if (this.loading) return;

      const isTopVisible = isElementInViewport({
        element: this.$refs.top as HTMLElement,
        container: this.containerElement,
        skipHorizontalCheck: this.skipHorizontalCheck,
      });

      const isBottomVisible = isElementInViewport({
        element: this.$refs.bottom as HTMLElement,
        container: this.containerElement,
        offset: this.threshold,
        containerOffset: this.containerThreshold,
        skipHorizontalCheck: this.skipHorizontalCheck,
      });

      if (isTopVisible) {
        this.$emit('on-scroll-top');
      } else if (isBottomVisible) {
        this.$emit('on-scroll-bottom');
      }

      this.scrollHandler();
    },
    getContainerElement(): HTMLElement | null {
      return this.container ? document.querySelector(this.container) : null;
    },
    getContainerElementStrict(): HTMLElement | Document {
      return this.getContainerElement() || document;
    },
  },
});
</script>
